import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axiosInstance from '../Services/axiosInstance';
import { taskService } from '../Services/taskService';
import type { TaskItemDto, ProjectDto } from '../types/tasks';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
  CardIconHeader,
} from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Skeleton } from '../components/ui/skeleton';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Compass,
  Plus,
  X,
  Pencil,
  Trash2,
  GripVertical,
  Maximize2,
  Minimize2,
  CheckSquare,
  Users,
  Database,
  FolderKanban,
  UserCircle,
  Rss,
  LayoutGrid,
  AppWindow,
} from 'lucide-react';
import { cn } from '../lib/utils';

// ── Widget definitions ──────────────────────────────────────────────

type WidgetId = 'tasks' | 'users' | 'subscriptions' | 'projects' | 'profile' | 'feed-sender';

interface WidgetDef {
  id: WidgetId;
  name: string;
  description: string;
  icon: React.ReactNode;
  color: string;
  size: 'small' | 'medium' | 'large';
}

const WIDGET_CATALOGUE: WidgetDef[] = [
  { id: 'tasks', name: 'My Tasks', description: 'Task overview with status breakdown', icon: <CheckSquare size={18} />, color: 'violet', size: 'medium' },
  { id: 'projects', name: 'Projects', description: 'Active projects overview', icon: <FolderKanban size={18} />, color: 'blue', size: 'medium' },
  { id: 'users', name: 'Users', description: 'Total user count', icon: <Users size={18} />, color: 'sky', size: 'small' },
  { id: 'subscriptions', name: 'Subscriptions', description: 'Active subscription count', icon: <Database size={18} />, color: 'emerald', size: 'small' },
  { id: 'profile', name: 'Profile', description: 'Current user info', icon: <UserCircle size={18} />, color: 'amber', size: 'small' },
  { id: 'feed-sender', name: 'Quick Feed', description: 'Send feed items to Navigator', icon: <Rss size={18} />, color: 'rose', size: 'medium' },
];

// ── Section model ───────────────────────────────────────────────────

interface QuickSection {
  id: string;
  name: string;
  widgets: WidgetId[];
  expandedWidget: WidgetId | null;
}

const STORAGE_KEY = 'narcisevo-navigator-sections';

const defaultSections: QuickSection[] = [
  { id: 'section-1', name: 'Overview', widgets: ['tasks', 'users', 'subscriptions'], expandedWidget: null },
  { id: 'section-2', name: 'Projects & Profile', widgets: ['projects', 'profile'], expandedWidget: null },
];

function loadSections(): QuickSection[] {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored) return JSON.parse(stored);
  } catch { /* ignore */ }
  return defaultSections;
}

function saveSections(sections: QuickSection[]) {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(sections));
}

// ── Widget components ───────────────────────────────────────────────

interface WidgetProps {
  expanded: boolean;
}

const TasksWidget: React.FC<WidgetProps> = ({ expanded }) => {
  const navigate = useNavigate();
  const [tasks, setTasks] = useState<TaskItemDto[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    taskService.getMyTasks()
      .then(setTasks)
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <Skeleton className="h-24 w-full" />;

  const todo = tasks.filter(t => t.status === 'Todo').length;
  const inProgress = tasks.filter(t => t.status === 'InProgress').length;
  const done = tasks.filter(t => t.status === 'Done').length;

  return (
    <div className="space-y-3">
      <div className="flex items-baseline gap-3">
        <span className="text-3xl font-bold">{tasks.length}</span>
        <span className="text-sm text-muted-foreground">total tasks</span>
      </div>
      <div className="flex gap-3 text-sm">
        <Badge variant="outline" className="gap-1"><span className="h-2 w-2 rounded-full bg-slate-400" />{todo} to do</Badge>
        <Badge variant="outline" className="gap-1"><span className="h-2 w-2 rounded-full bg-blue-500" />{inProgress} in progress</Badge>
        <Badge variant="outline" className="gap-1"><span className="h-2 w-2 rounded-full bg-emerald-500" />{done} done</Badge>
      </div>
      {expanded && tasks.length > 0 && (
        <div className="mt-4 space-y-2 max-h-[300px] overflow-y-auto">
          {tasks.slice(0, 10).map(task => (
            <div
              key={task.id}
              className="flex items-center justify-between rounded-lg border p-3 hover:bg-muted/50 cursor-pointer transition-colors"
              onClick={() => navigate(`/projects/${task.projectId}`)}
            >
              <div className="min-w-0">
                <p className="text-sm font-medium truncate">{task.title}</p>
                <p className="text-xs text-muted-foreground">{task.projectName}</p>
              </div>
              <Badge variant={task.priority === 'High' || task.priority === 'Urgent' ? 'destructive' : 'secondary'} className="text-xs shrink-0">
                {task.priority}
              </Badge>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

const ProjectsWidget: React.FC<WidgetProps> = ({ expanded }) => {
  const navigate = useNavigate();
  const [projects, setProjects] = useState<ProjectDto[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    taskService.getMyProjects()
      .then(setProjects)
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <Skeleton className="h-24 w-full" />;

  return (
    <div className="space-y-3">
      <div className="flex items-baseline gap-3">
        <span className="text-3xl font-bold">{projects.length}</span>
        <span className="text-sm text-muted-foreground">active projects</span>
      </div>
      {(expanded ? projects : projects.slice(0, 3)).map(project => (
        <div
          key={project.id}
          className="flex items-center justify-between rounded-lg border p-3 hover:bg-muted/50 cursor-pointer transition-colors"
          onClick={() => navigate(`/projects/${project.id}`)}
        >
          <div className="min-w-0">
            <p className="text-sm font-medium truncate">{project.name}</p>
            <p className="text-xs text-muted-foreground truncate">{project.description}</p>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <Badge variant="secondary" className="text-xs">{project.taskCount} tasks</Badge>
            <Badge variant="outline" className="text-xs">{project.memberCount} members</Badge>
          </div>
        </div>
      ))}
    </div>
  );
};

const UsersWidget: React.FC<WidgetProps> = () => {
  const [count, setCount] = useState<number | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axiosInstance.get<{ count: number }>('/api/Statistics/users')
      .then(res => setCount(res.data.count))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <Skeleton className="h-16 w-full" />;

  return (
    <div>
      <div className="text-3xl font-bold">{count ?? '—'}</div>
      <div className="text-sm text-muted-foreground">Total users</div>
    </div>
  );
};

const SubscriptionsWidget: React.FC<WidgetProps> = () => {
  const [count, setCount] = useState<number | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axiosInstance.get<{ count: number }>('/api/Statistics/subscriptions')
      .then(res => setCount(res.data.count))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <Skeleton className="h-16 w-full" />;

  return (
    <div>
      <div className="text-3xl font-bold">{count ?? '—'}</div>
      <div className="text-sm text-muted-foreground">Active subscriptions</div>
    </div>
  );
};

const ProfileWidget: React.FC<WidgetProps> = () => {
  const [profile, setProfile] = useState<{ name?: string; email?: string; role?: string } | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axiosInstance.get('/api/Profile')
      .then(res => setProfile(res.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <Skeleton className="h-16 w-full" />;

  return (
    <div className="space-y-1">
      <div className="text-lg font-bold">{profile?.name ?? '—'}</div>
      <div className="text-sm text-muted-foreground">{profile?.email}</div>
      {profile?.role && <Badge variant="secondary" className="text-xs mt-1">{profile.role}</Badge>}
    </div>
  );
};

const FeedSenderWidget: React.FC<WidgetProps> = () => {
  const navigate = useNavigate();

  return (
    <div className="space-y-3">
      <p className="text-sm text-muted-foreground">
        Send feed items to the Access Workspace feed with support for Actions Required, inline actions, and due dates.
      </p>
      <Button size="sm" className="gap-2" onClick={() => navigate('/feed')}>
        <Rss className="h-4 w-4" />
        Open Feed Sender
      </Button>
    </div>
  );
};

const WIDGET_COMPONENTS: Record<WidgetId, React.FC<WidgetProps>> = {
  tasks: TasksWidget,
  projects: ProjectsWidget,
  users: UsersWidget,
  subscriptions: SubscriptionsWidget,
  profile: ProfileWidget,
  'feed-sender': FeedSenderWidget,
};

// ── Color utility ───────────────────────────────────────────────────

function widgetColorClasses(color: string) {
  const map: Record<string, { bg: string; text: string; bar: string }> = {
    violet: { bg: 'bg-violet-500/10', text: 'text-violet-500', bar: 'from-violet-500/80 to-violet-500/20' },
    blue: { bg: 'bg-blue-500/10', text: 'text-blue-500', bar: 'from-blue-500/80 to-blue-500/20' },
    sky: { bg: 'bg-sky-500/10', text: 'text-sky-500', bar: 'from-sky-500/80 to-sky-500/20' },
    emerald: { bg: 'bg-emerald-500/10', text: 'text-emerald-500', bar: 'from-emerald-500/80 to-emerald-500/20' },
    amber: { bg: 'bg-amber-500/10', text: 'text-amber-500', bar: 'from-amber-500/80 to-amber-500/20' },
    rose: { bg: 'bg-rose-500/10', text: 'text-rose-500', bar: 'from-rose-500/80 to-rose-500/20' },
  };
  return map[color] ?? map.blue;
}

// ── Main Navigator page ─────────────────────────────────────────────

const Navigator: React.FC = () => {
  const [sections, setSections] = useState<QuickSection[]>(loadSections);
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [addTargetSection, setAddTargetSection] = useState<string | null>(null);
  const [renameDialogOpen, setRenameDialogOpen] = useState(false);
  const [renameSectionId, setRenameSectionId] = useState<string | null>(null);
  const [renameValue, setRenameValue] = useState('');
  const [newSectionDialogOpen, setNewSectionDialogOpen] = useState(false);
  const [newSectionName, setNewSectionName] = useState('');

  // Persist on change
  useEffect(() => {
    saveSections(sections);
  }, [sections]);

  // ── Section operations ──────────────────────────────────────────

  const addSection = useCallback(() => {
    if (!newSectionName.trim()) return;
    const section: QuickSection = {
      id: `section-${Date.now()}`,
      name: newSectionName.trim(),
      widgets: [],
      expandedWidget: null,
    };
    setSections(prev => [...prev, section]);
    setNewSectionName('');
    setNewSectionDialogOpen(false);
  }, [newSectionName]);

  const deleteSection = useCallback((sectionId: string) => {
    setSections(prev => prev.filter(s => s.id !== sectionId));
  }, []);

  const renameSection = useCallback(() => {
    if (!renameSectionId || !renameValue.trim()) return;
    setSections(prev => prev.map(s =>
      s.id === renameSectionId ? { ...s, name: renameValue.trim() } : s
    ));
    setRenameDialogOpen(false);
  }, [renameSectionId, renameValue]);

  // ── Widget operations ───────────────────────────────────────────

  const addWidget = useCallback((sectionId: string, widgetId: WidgetId) => {
    setSections(prev => prev.map(s =>
      s.id === sectionId ? { ...s, widgets: [...s.widgets, widgetId] } : s
    ));
    setAddDialogOpen(false);
  }, []);

  const removeWidget = useCallback((sectionId: string, index: number) => {
    setSections(prev => prev.map(s =>
      s.id === sectionId ? { ...s, widgets: s.widgets.filter((_, i) => i !== index), expandedWidget: null } : s
    ));
  }, []);

  const toggleExpand = useCallback((sectionId: string, widgetId: WidgetId) => {
    setSections(prev => prev.map(s =>
      s.id === sectionId ? { ...s, expandedWidget: s.expandedWidget === widgetId ? null : widgetId } : s
    ));
  }, []);

  // ── Available widgets for a section (not already added) ─────────

  const availableWidgets = useCallback((section: QuickSection) => {
    return WIDGET_CATALOGUE.filter(w => !section.widgets.includes(w.id));
  }, []);

  return (
    <div className="space-y-6">
      {/* Header */}
      <Card>
        <CardIconHeader icon={<Compass size={22} className="text-primary" />}>
          <CardTitle>Evo Navigator</CardTitle>
          <CardDescription>
            Your personalised command centre. Organise MFEs into customisable Quick Sections
            to build your own workspace — group related tools, pick up where you left off,
            and dive deep into workflows without leaving Navigator.
          </CardDescription>
        </CardIconHeader>
      </Card>

      {/* Quick Sections */}
      {sections.map(section => {
        const isExpanded = section.expandedWidget !== null;

        return (
          <Card key={section.id} className="animate-fade-in-up">
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <GripVertical className="h-4 w-4 text-muted-foreground/50" />
                  <CardTitle className="text-lg">{section.name}</CardTitle>
                  <Badge variant="secondary" className="text-xs">{section.widgets.length} widgets</Badge>
                </div>
                <div className="flex items-center gap-1">
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-8 w-8"
                    onClick={() => {
                      setAddTargetSection(section.id);
                      setAddDialogOpen(true);
                    }}
                    disabled={availableWidgets(section).length === 0}
                    title="Add widget"
                  >
                    <Plus className="h-4 w-4" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-8 w-8"
                    onClick={() => {
                      setRenameSectionId(section.id);
                      setRenameValue(section.name);
                      setRenameDialogOpen(true);
                    }}
                    title="Rename section"
                  >
                    <Pencil className="h-3.5 w-3.5" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-8 w-8 text-destructive/70 hover:text-destructive"
                    onClick={() => deleteSection(section.id)}
                    title="Delete section"
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                  </Button>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              {section.widgets.length === 0 ? (
                <div className="rounded-lg border-2 border-dashed border-muted-foreground/20 py-12">
                  <div className="flex flex-col items-center gap-3 text-center">
                    <div className="rounded-full bg-muted/80 p-3 ring-1 ring-border/50">
                      <LayoutGrid size={24} className="text-muted-foreground" />
                    </div>
                    <p className="font-medium text-muted-foreground">No widgets yet</p>
                    <p className="text-sm text-muted-foreground/75">
                      Add widgets from the catalogue to build your workspace
                    </p>
                    <Button
                      variant="outline"
                      size="sm"
                      className="gap-1.5 mt-2"
                      onClick={() => {
                        setAddTargetSection(section.id);
                        setAddDialogOpen(true);
                      }}
                    >
                      <Plus className="h-3.5 w-3.5" />
                      Add Widget
                    </Button>
                  </div>
                </div>
              ) : (
                <div className={cn(
                  "grid gap-4",
                  isExpanded
                    ? "grid-cols-1"
                    : "grid-cols-1 md:grid-cols-2 lg:grid-cols-3"
                )}>
                  {section.widgets.map((widgetId, index) => {
                    const def = WIDGET_CATALOGUE.find(w => w.id === widgetId);
                    if (!def) return null;

                    const isThisExpanded = section.expandedWidget === widgetId;
                    if (isExpanded && !isThisExpanded) return null;

                    const WidgetComponent = WIDGET_COMPONENTS[widgetId];
                    const colors = widgetColorClasses(def.color);

                    return (
                      <Card
                        key={`${widgetId}-${index}`}
                        className={cn(
                          "group relative overflow-hidden transition-all duration-200",
                          isThisExpanded && "col-span-full"
                        )}
                      >
                        <div className={cn("absolute bottom-0 left-0 right-0 h-1 bg-gradient-to-r", colors.bar)} />
                        <CardHeader className="pb-2">
                          <div className="flex items-center justify-between">
                            <CardTitle className="flex items-center gap-2.5 text-base">
                              <div className={cn("flex h-8 w-8 items-center justify-center rounded-lg transition-transform duration-200 group-hover:scale-110", colors.bg, colors.text)}>
                                {def.icon}
                              </div>
                              {def.name}
                            </CardTitle>
                            <div className="flex items-center gap-0.5 opacity-0 group-hover:opacity-100 transition-opacity">
                              <Button
                                variant="ghost"
                                size="icon"
                                className="h-7 w-7"
                                onClick={() => toggleExpand(section.id, widgetId)}
                                title={isThisExpanded ? 'Collapse' : 'Expand full width'}
                              >
                                {isThisExpanded ? <Minimize2 className="h-3.5 w-3.5" /> : <Maximize2 className="h-3.5 w-3.5" />}
                              </Button>
                              <Button
                                variant="ghost"
                                size="icon"
                                className="h-7 w-7 text-destructive/70 hover:text-destructive"
                                onClick={() => removeWidget(section.id, index)}
                                title="Remove widget"
                              >
                                <X className="h-3.5 w-3.5" />
                              </Button>
                            </div>
                          </div>
                        </CardHeader>
                        <CardContent>
                          <WidgetComponent expanded={isThisExpanded} />
                        </CardContent>
                      </Card>
                    );
                  })}
                </div>
              )}
            </CardContent>
          </Card>
        );
      })}

      {/* Add Section Button */}
      <Button
        variant="outline"
        className="w-full gap-2 border-dashed h-12"
        onClick={() => setNewSectionDialogOpen(true)}
      >
        <Plus className="h-4 w-4" />
        Add Quick Section
      </Button>

      {/* MFE Metadata Info */}
      <Card className="border-dashed">
        <CardHeader className="pb-2">
          <CardTitle className="flex items-center gap-2 text-base">
            <AppWindow className="h-4 w-4 text-muted-foreground" />
            MFE Quick Section Metadata
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-muted-foreground mb-3">
            To make your MFE available as a Quick Section in Evo Navigator, add the following to your
            MFE's <code className="rounded bg-muted px-1.5 py-0.5 text-xs font-mono">package.json</code> under
            the <code className="rounded bg-muted px-1.5 py-0.5 text-xs font-mono">access-evo</code> field:
          </p>
          <pre className="rounded-lg bg-muted/50 p-3 text-xs font-mono overflow-x-auto">
{`"access-evo": {
  "friendlyName": "Narcis EvoApp",
  "quickSection": {
    "size": "medium"  // "small" | "medium" | "large"
  }
}`}
          </pre>
        </CardContent>
      </Card>

      {/* ── Add Widget Dialog ──────────────────────────────────────── */}
      <Dialog open={addDialogOpen} onOpenChange={setAddDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Add Widget</DialogTitle>
          </DialogHeader>
          <div className="space-y-2 max-h-[400px] overflow-y-auto py-2">
            {addTargetSection && availableWidgets(sections.find(s => s.id === addTargetSection)!).map(widget => {
              const colors = widgetColorClasses(widget.color);
              return (
                <div
                  key={widget.id}
                  className="flex items-center gap-3 rounded-lg border p-3 hover:bg-muted/50 cursor-pointer transition-colors"
                  onClick={() => addWidget(addTargetSection, widget.id)}
                >
                  <div className={cn("flex h-9 w-9 items-center justify-center rounded-lg shrink-0", colors.bg, colors.text)}>
                    {widget.icon}
                  </div>
                  <div className="min-w-0">
                    <p className="text-sm font-medium">{widget.name}</p>
                    <p className="text-xs text-muted-foreground">{widget.description}</p>
                  </div>
                  <Badge variant="outline" className="ml-auto text-xs shrink-0">{widget.size}</Badge>
                </div>
              );
            })}
            {addTargetSection && availableWidgets(sections.find(s => s.id === addTargetSection)!).length === 0 && (
              <p className="text-sm text-muted-foreground text-center py-4">All widgets have been added to this section.</p>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* ── Rename Section Dialog ──────────────────────────────────── */}
      <Dialog open={renameDialogOpen} onOpenChange={setRenameDialogOpen}>
        <DialogContent className="sm:max-w-sm">
          <DialogHeader>
            <DialogTitle>Rename Section</DialogTitle>
          </DialogHeader>
          <div className="space-y-2 py-2">
            <Label htmlFor="rename-input">Section Name</Label>
            <Input
              id="rename-input"
              value={renameValue}
              onChange={e => setRenameValue(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && renameSection()}
              autoFocus
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setRenameDialogOpen(false)}>Cancel</Button>
            <Button onClick={renameSection} disabled={!renameValue.trim()}>Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ── New Section Dialog ─────────────────────────────────────── */}
      <Dialog open={newSectionDialogOpen} onOpenChange={setNewSectionDialogOpen}>
        <DialogContent className="sm:max-w-sm">
          <DialogHeader>
            <DialogTitle>New Quick Section</DialogTitle>
          </DialogHeader>
          <div className="space-y-2 py-2">
            <Label htmlFor="new-section-input">Section Name</Label>
            <Input
              id="new-section-input"
              placeholder="e.g. Morning Dashboard"
              value={newSectionName}
              onChange={e => setNewSectionName(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && addSection()}
              autoFocus
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setNewSectionDialogOpen(false)}>Cancel</Button>
            <Button onClick={addSection} disabled={!newSectionName.trim()}>Create</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Navigator;
